// Who is signed in, their workspaces and the offered models. Loaded once; `refresh()` after a
// change that alters them (a workspace renamed by onboarding, membership changes).
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, ApiError, setUnauthorizedHandler } from './api';
import type { Me, WorkspaceRef } from './types';

interface SessionValue {
  me: Me;
  refresh: () => Promise<void>;
  workspace: (slug: string | undefined) => WorkspaceRef | undefined;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children, fallback }: { children: ReactNode; fallback: (s: State) => ReactNode }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const navigate = useNavigate();
  const location = useLocation();

  const load = useCallback(async () => {
    try {
      const me = await api<Me>('/api/me', { noRedirect: true });
      setState({ kind: 'ready', me });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setState({ kind: 'signed-out' });
      else setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setUnauthorizedHandler(() => setState({ kind: 'signed-out' }));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    if (state.kind === 'signed-out') {
      const back = location.pathname + location.search;
      navigate(`/login?return_to=${encodeURIComponent(back)}`, { replace: true });
    }
  }, [state.kind, location.pathname, location.search, navigate]);

  if (state.kind !== 'ready') return <>{fallback(state)}</>;
  const value: SessionValue = {
    me: state.me,
    refresh: load,
    workspace: (slug) => state.me.workspaces.find((w) => w.slug === slug),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export type State =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; me: Me };

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
